import { createBrowserHistory, History } from 'history';
import { configureStore } from '@reduxjs/toolkit';
import { Hub } from '@aws-amplify/core';
import { CognitoConfiguration, getConfiguration } from './staticConfiguration';
import user from './UserManagement';
import classrooms from './Classrooms';
import messages from './Messages';
import lessons from './Lessons';
import { session } from './Session';
import events, { push, AuthEvent } from './AuthenticationEvents';
import audioPlayer from './components/AudioPlayer/AudioPlayer';
import adminCoTeachers from './Administrator/adminCoTeachersSlice';

const store = configureStore({
  reducer: {
    audioPlayer,
    user,
    classrooms,
    messages,
    lessons,
    session,
    events,
    adminCoTeachers,
  },
  // CAUTION: we are explicitly turning off the warnings about putting in
  // non-serizliable objects into the store. This is because we are using
  // the CognitoUserProfileType which is an object with functions that we want
  // access to such as getSignInUserSession.
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),
});

export const myBrowserHistory: History = createBrowserHistory();
export const cognitoConfiguration = getConfiguration('cognito') as CognitoConfiguration;

// Import data residency aware initialization
import { initializeDataResidencyAwareAuth } from '@peekapak/data-residency';

// The Amplify Hub is app-global state, like the store, so the app owns the
// wiring. Registered once at module load - before initializeApplicationState
// runs - so no auth event can fire into a listener that is not yet attached.
Hub.listen('auth', pushEventIntoQueue);

/**
 * Resolve this browser's data residency and configure Amplify for it.
 *
 * There is deliberately no legacy fallback here any more. The one that used to
 * live in this file called Amplify.configure with the US-only pool ids from
 * staticConfiguration and WITHOUT the `cookieStorage` block - so it could not
 * serve an ARE account at all, and it silently moved the Cognito tokens from
 * cookies to localStorage, which is where the peekapak.com -> /play session
 * handoff reads them from. A failure here must surface, not quietly degrade
 * into a session the sibling app cannot see.
 */
export async function initializeApplicationState() {
  console.debug(`Initializing application state with data residency awareness`);

  await initializeDataResidencyAwareAuth();
  console.log('✅ Data residency aware auth initialized');
}

function pushEventIntoQueue({ payload }: { payload: AuthEvent }) {
  console.info(`%c OauthLogin pushing event `, 'background: #222; color: #bada55', payload);

  getStore().dispatch(push(payload));
}

export function getStore() {
  return store;
}

// Export push for use in DataResidencyAwareAuthHelpers
export { push } from './AuthenticationEvents';

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
